import { IconButton } from '@mui/material';
import { FieldArray, useFormikContext } from 'formik';
import React, { useRef } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { FormikDropdownField, FormikTextField } from '@components/FormikFields';
import Icon from '@components/Icon/Icon';
import InputLabel from '@components/InputLabel';
import RoundedButton from '@components/RoundedButton/index';

import {
  STRENGTH_UNITS,
  UNKNOWN_UNIT,
  formatUnitLabel,
  isVolumeUnit,
  unitOptions,
} from '@constants/medication-dosage';

import {
  type MedicationIngredientRow,
  emptyIngredientRow,
  formatStrengths,
} from '@utils/fhir-mappers/medicationIngredients';

import {
  DynamicNoun,
  RemoveCell,
  RowHelp,
  SentenceCell,
  SentenceErrors,
  SentenceRow,
  SubsectionLabel,
} from './Fields.style';
import {
  AddRowHint,
  AddRowLine,
  CombinedStrengthLine,
  IngredientRowBox,
  IngredientsWrapper,
  ReadWrapper,
  SummaryLine,
} from './Ingredients.style';
import { useDoseFormNoun } from './useDoseFormNoun';
import { useSentenceErrors } from './useSentenceErrors';

const messages = defineMessages({
  ingredients: {
    id: 'medicationIngredients.ingredients',
    defaultMessage: 'Strength',
  },
  /**
   * The whole strength row as one sentence. `per` is the amount input, present
   * only for a form that is itself measured ("each 5 mL contains 250 mg"); a
   * counted form is always per one, so it collapses to "each tablet contains".
   */
  strengthSentence: {
    id: 'medicationIngredients.strengthSentence',
    defaultMessage: 'Each {per}{form} contains {amount}{unit}',
  },
  /**
   * The same sentence with the ingredient named, for a combination product.
   * Written out in full rather than appended as a chunk, so a translator sees
   * one whole sentence and can move the name where the language wants it.
   */
  strengthSentenceNamed: {
    id: 'medicationIngredients.strengthSentenceNamed',
    defaultMessage: 'Each {per}{form} contains {amount}{unit} of {name}',
  },
  ingredientName: {
    id: 'medicationIngredients.ingredientName',
    defaultMessage: 'Ingredient',
  },
  /**
   * Says what the box is for, since the sentence around it reads as prose
   * ("of ___") rather than as a label.
   */
  ingredientNamePlaceholder: {
    id: 'medicationIngredients.ingredientNamePlaceholder',
    defaultMessage: 'Ingredient name',
  },
  /**
   * The ingredient boxes are optional, and a placeholder can't say so: it
   * disappears as soon as someone types, and this one names the box instead of
   * the fallback. So the rule lives here, where it stays put.
   */
  ingredientNameHint: {
    id: 'medicationIngredients.ingredientNameHint',
    defaultMessage:
      'Leave an ingredient blank to name that part after the medication.',
  },
  strengthValue: {
    id: 'medicationIngredients.strengthValue',
    defaultMessage: 'Amount',
  },
  strengthUnit: {
    id: 'medicationIngredients.strengthUnit',
    defaultMessage: 'Unit',
  },
  strengthPerValue: {
    id: 'medicationIngredients.strengthPerValue',
    defaultMessage: 'per',
  },
  add: {
    id: 'medicationIngredients.add',
    defaultMessage: 'Add combination dose',
  },
  addHint: {
    id: 'medicationIngredients.addHint',
    defaultMessage: 'One row per part, in the order they appear in the name.',
  },
  combined: {
    id: 'medicationIngredients.combined',
    defaultMessage: 'Combined strength',
  },
  /**
   * Shown when no strength was structured but the dosage carries one as free
   * text, which is how the migration left brand-name combination products.
   */
  unstructured: {
    id: 'medicationIngredients.unstructured',
    defaultMessage: 'No strength recorded here. The dosage says: {text}',
  },
  /** Distinguishes otherwise identical per-row controls for screen readers. */
  rowGroup: {
    id: 'medicationIngredients.rowGroup',
    defaultMessage: 'Part {n}',
  },
  remove: {
    id: 'medicationIngredients.remove',
    defaultMessage: 'Remove part {n}',
  },
  none: { id: 'medicationIngredients.none', defaultMessage: '—' },
});

const blankOption = (label: string) => ({ k: '', v: label });

/** One strength row's validation messages, collected under it. */
function StrengthRowErrors({ index }: { index: number }) {
  const messages = useSentenceErrors([
    `ingredients.${index}.strengthValue`,
    `ingredients.${index}.strengthUnit`,
    `ingredients.${index}.strengthPerValue`,
  ]);
  if (!messages.length) return null;
  return (
    <SentenceErrors role="alert" data-testid="ingredient-row-errors">
      {messages.join(' ')}
    </SentenceErrors>
  );
}

interface IngredientsProps {
  isEditing: boolean;
}

/**
 * Strength editor for the product a MedicationStatement refers to.
 *
 * These rows belong to the contained `Medication`, not to the dosage: the
 * strength is how much drug is in one tablet, while the quantity is how many
 * tablets are taken. A combination product is simply more than one row, and
 * renders canonically as `875/125 mg`.
 *
 * The ingredient name is asked for only once there is more than one row. A
 * single-ingredient product *is* the record's title, so a box repeating it was
 * cost with no payoff (VEG-760) — but on a combination product the parts have
 * their own names, and curators who know them had nowhere to put them. Left
 * blank, a row is named after the product on save, so a two-part tablet nobody
 * can break down still works: its rows are read against the medication name
 * positionally, which is what the hint under the add button says.
 *
 * The "per what" half of the strength is no longer a dropdown either. It is
 * whatever the Form says (VEG-760), so a tablet reads "each tablet contains
 * 875 mg" with nothing to fill in, and only a measured form — a suspension, an
 * insulin — asks for the amount it is measured against.
 */
export default function Ingredients({ isEditing }: IngredientsProps) {
  const { formatMessage } = useIntl();
  const doseFormNoun = useDoseFormNoun();
  // Optional: the auto-curation form builds its values from
  // RESOURCE_CURATION_CONFIG defaults, which OCR may not populate.
  const { values, setFieldValue } = useFormikContext<{
    ingredients?: MedicationIngredientRow[];
    dosageSteps?: { asDirected?: string }[];
    doseForm?: string;
  }>();
  const rows = values.ingredients ?? [];
  const doseForm = values.doseForm ?? '';
  const combined = formatStrengths(rows);

  // A brand-name combination product often has its strength only in
  // `Dosage.text`, with no ingredient[] at all. Rendering a bare dash here
  // implied no strength was on file when it was simply somewhere else.
  const freeTextStrength = combined
    ? ''
    : (values.dosageSteps?.[0]?.asDirected ?? '').trim();

  /**
   * Stable keys for the rows. `MedicationIngredientRow` has no id, and keying on
   * the index reuses DOM inputs across rows: removing a middle row while focus
   * sits in a later one silently turns that input into the editor for a
   * different ingredient. Held outside the data so nothing extra reaches FHIR.
   */
  const keys = useRef<number[]>([]);
  const nextKey = useRef(0);
  while (keys.current.length < rows.length) {
    keys.current.push(nextKey.current);
    nextKey.current += 1;
  }

  if (!isEditing) {
    // A row with no name of its own is named after the product on save, and the
    // form blanks those again on load, so anything left here is a real
    // ingredient name worth printing. Nothing repeats the record title.
    const lines = rows
      .map((row) =>
        [
          row.name.trim(),
          row.strengthValue &&
            `${row.strengthValue} ${formatUnitLabel(
              row.strengthUnit,
              Number(row.strengthValue)
            )}`,
          // Only worth showing when it isn't "per one".
          row.strengthPerValue &&
            row.strengthPerValue !== '1' &&
            row.strengthPerUnit &&
            `${formatMessage(messages.strengthPerValue)} ${
              row.strengthPerValue
            } ${formatUnitLabel(
              row.strengthPerUnit,
              Number(row.strengthPerValue)
            )}`,
        ]
          .filter(Boolean)
          .join(' ')
      )
      .filter(Boolean);

    return (
      <ReadWrapper>
        <InputLabel label={formatMessage(messages.ingredients)} />
        {lines.length ? (
          lines.map((line, i) => (
            <SummaryLine
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              data-testid="ingredient-summary"
            >
              {line}
            </SummaryLine>
          ))
        ) : freeTextStrength ? (
          <SummaryLine data-testid="ingredient-unstructured">
            {formatMessage(messages.unstructured, { text: freeTextStrength })}
          </SummaryLine>
        ) : (
          <SummaryLine>-</SummaryLine>
        )}
        {/* The canonical X/Y form only says something a single row doesn't. */}
        {rows.length > 1 && combined && (
          <CombinedStrengthLine data-testid="ingredient-combined-strength">
            {`${formatMessage(messages.combined)}: ${combined}`}
          </CombinedStrengthLine>
        )}
      </ReadWrapper>
    );
  }

  const none = formatMessage(messages.none);

  // A number input makes Formik coerce the value to a JS number, but the row
  // fields are strings (matching how containedToIngredientRows hydrates them).
  const keepString = (name: string) => (value: number | string) =>
    setFieldValue(name, String(value));

  /**
   * Whether this row asks what the strength is measured against. A form quoted
   * as a volume is a concentration and needs it; nothing else does — IU is a
   * measured unit but insulin is not dosed "per 5 IU". A stored row that already
   * carries something other than "per 1" keeps its input regardless, or the
   * value would sit off-screen and still be saved.
   */
  const showsPerValue = (row: MedicationIngredientRow) =>
    isVolumeUnit(doseForm) ||
    Boolean(row.strengthPerValue && row.strengthPerValue !== '1');

  return (
    <IngredientsWrapper>
      <SubsectionLabel>
        <InputLabel label={formatMessage(messages.ingredients)} />
      </SubsectionLabel>
      <FieldArray
        name="ingredients"
        render={(arrayHelpers) => (
          <>
            {rows.map((row, index) => {
              const showPer = showsPerValue(row);
              // Only worth asking on a combination product: on a single row the
              // answer is the record's own title.
              const showName = rows.length > 1;
              const noun = doseFormNoun(
                doseForm,
                showPer ? Number(row.strengthPerValue || 1) : 1
              );
              return (
                <IngredientRowBox
                  key={keys.current[index]}
                  data-testid="ingredient-row"
                  // Every row repeats the same field labels, so without this a
                  // screen reader can't tell part 2's Amount from part 1's.
                  role="group"
                  aria-label={formatMessage(messages.rowGroup, {
                    n: index + 1,
                  })}
                >
                  <SentenceRow>
                    <FormattedMessage
                      {...(showName
                        ? messages.strengthSentenceNamed
                        : messages.strengthSentence)}
                      values={{
                        form: <DynamicNoun key="form">{noun}</DynamicNoun>,
                        per: showPer ? (
                          <SentenceCell $size="number" key="per">
                            <FormikTextField
                              type="number"
                              label={formatMessage(messages.strengthPerValue)}
                              name={`ingredients.${index}.strengthPerValue`}
                              onChange={keepString(
                                `ingredients.${index}.strengthPerValue`
                              )}
                            />
                          </SentenceCell>
                        ) : null,
                        amount: (
                          <SentenceCell $size="number" key="amount">
                            <FormikTextField
                              type="number"
                              label={formatMessage(messages.strengthValue)}
                              name={`ingredients.${index}.strengthValue`}
                              onChange={keepString(
                                `ingredients.${index}.strengthValue`
                              )}
                            />
                          </SentenceCell>
                        ),
                        unit: (
                          <SentenceCell $size="unit" key="unit">
                            <FormikDropdownField
                              ariaLabel={formatMessage(messages.strengthUnit)}
                              name={`ingredients.${index}.strengthUnit`}
                              options={[
                                blankOption(none),
                                ...unitOptions(
                                  STRENGTH_UNITS,
                                  row.strengthUnit
                                ),
                              ]}
                            />
                          </SentenceCell>
                        ),
                        name: showName ? (
                          <SentenceCell $size="text" key="name">
                            <FormikTextField
                              label={formatMessage(messages.ingredientName)}
                              name={`ingredients.${index}.name`}
                              placeholder={formatMessage(
                                messages.ingredientNamePlaceholder
                              )}
                            />
                          </SentenceCell>
                        ) : null,
                      }}
                    />
                    {/* The first row is the product itself, so it always stays. */}
                    {index > 0 && (
                      <RemoveCell>
                        <IconButton
                          aria-label={formatMessage(messages.remove, {
                            n: index + 1,
                          })}
                          onClick={() => {
                            keys.current.splice(index, 1);
                            arrayHelpers.remove(index);
                          }}
                        >
                          <Icon name="trash" size="sm" type="fas" />
                        </IconButton>
                      </RemoveCell>
                    )}
                  </SentenceRow>
                  <StrengthRowErrors index={index} />
                </IngredientRowBox>
              );
            })}

            {/* One note for the group: the same text under every row would
                stack a caption between each sentence and hide the pattern the
                rows are meant to show. */}
            {rows.length > 1 && (
              <RowHelp data-testid="ingredient-name-hint">
                {formatMessage(messages.ingredientNameHint)}
              </RowHelp>
            )}

            <AddRowLine>
              <RoundedButton
                autoSize
                fontSize={13}
                height={40}
                iconName="plus"
                // Seeded here rather than from an effect: an effect keyed on
                // the row count also fires on mount and on a refetch, which is
                // how an untouched save started writing "per 1 mg".
                onClick={() =>
                  arrayHelpers.push({
                    ...emptyIngredientRow(),
                    strengthPerUnit: doseForm === UNKNOWN_UNIT ? '' : doseForm,
                  })
                }
                type="button"
              >
                {formatMessage(messages.add)}
              </RoundedButton>
              <AddRowHint>{formatMessage(messages.addHint)}</AddRowHint>
            </AddRowLine>
          </>
        )}
      />

      {rows.length > 1 && combined && (
        <CombinedStrengthLine data-testid="ingredient-combined-strength">
          {`${formatMessage(messages.combined)}: ${combined}`}
        </CombinedStrengthLine>
      )}
    </IngredientsWrapper>
  );
}
